import qs from 'qs';
import { AxiosInstance } from 'axios';
import { OffsetPaginationInput } from '@app/data/inputs';
import { CollectionResource } from '@app/data/resources';
import { BrandProfileImageHashRef, BrandProfileRef } from './refs';
import {
    BrandProfilesFilterInput,
    CreateBrandProfileInput,
    RestoreBrandProfileInput,
    UpdateBrandProfileInformationInput,
} from './inputs';
import { BrandProfileListItemResource, BrandProfileResource } from './resources';

export default class BrandProfile {
    private basePath = '/api/brand-profile';
    private http: AxiosInstance;

    constructor(http: AxiosInstance) {
        this.http = http;
    }

    // Get brand profile list
    get = async (
        pagination: OffsetPaginationInput,
        filter: BrandProfilesFilterInput
    ): Promise<CollectionResource<BrandProfileListItemResource, {}>> => {
        const query = qs.stringify({ ...pagination, ...(filter || {}) });

        const { data } = await this.http.get(`${this.basePath}/?${query}`);

        return data;
    };

    // Create brand profile
    create = async (input: CreateBrandProfileInput): Promise<BrandProfileRef> => {
        const { data } = await this.http.post(`${this.basePath}/`, input);

        return data;
    };

    // Get single brand profile
    getById = async (id: ID): Promise<BrandProfileResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}`);

        return data;
    };

    // Patch brand profile info
    update = async (id: ID, input: UpdateBrandProfileInformationInput): Promise<BrandProfileRef> => {
        const { data } = await this.http.patch(`${this.basePath}/${id}`, input);

        return data;
    };

    // Uploads and sets brand profile image
    uploadAndSetImage = async (id: ID, image: File): Promise<BrandProfileImageHashRef> => {
        // Prepare form
        const form = new FormData();
        form.append('file', image);

        // Upload
        const { data } = await this.http.post(`${this.basePath}/${id}/image`, form, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return data;
    };

    // Removes current brand profile image
    removeImage = async (id: ID): Promise<BrandProfileRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/image`);

        return data;
    };

    // Archive brand profile
    archive = async (id: ID): Promise<BrandProfileRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}`);

        return data;
    };

    // Restore archived brand profile
    restore = async (id: ID, input?: RestoreBrandProfileInput): Promise<BrandProfileRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/restore`, input);

        return data;
    };
}
